import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import type { Position, PositionFormData } from "~/types/wms";

interface PositionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position?: Position;
  rackId?: string;
  onSubmit: (data: PositionFormData) => void;
  isLoading: boolean;
}

export function PositionDialog({
  open,
  onOpenChange,
  position,
  rackId,
  onSubmit,
  isLoading,
}: PositionDialogProps) {
  const [formData, setFormData] = useState<PositionFormData>({
    rack_id: "",
    code: "",
    level: 1,
    is_active: true,
  });

  useEffect(() => {
    if (position) {
      setFormData({
        rack_id: position.rack_id,
        code: position.code,
        barcode: position.barcode || undefined,
        level: position.level || 1,
        max_weight_kg: position.max_weight_kg || undefined,
        max_volume_m3: position.max_volume_m3 || undefined,
        distance_from_shipping: position.distance_from_shipping || undefined,
        ergonomic_score: position.ergonomic_score || undefined,
        is_active: position.is_active,
      });
    } else {
      setFormData({
        rack_id: rackId || "",
        code: "",
        level: 1,
        is_active: true,
      });
    }
  }, [position, rackId, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {position ? "Editar Posición" : "Nueva Posición"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="code" className="text-right">
                Código *
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="col-span-3"
                placeholder="L1-P01"
                required
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="barcode" className="text-right">
                Barcode
              </Label>
              <Input
                id="barcode"
                value={formData.barcode || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    barcode: e.target.value || undefined,
                  })
                }
                className="col-span-3"
                placeholder="Auto-generado si vacío"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="level" className="text-right">
                Nivel *
              </Label>
              <Input
                id="level"
                type="number"
                min={1}
                value={formData.level}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    level: parseInt(e.target.value) || 1,
                  })
                }
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="max_weight_kg" className="text-right">
                Peso Máx (kg)
              </Label>
              <Input
                id="max_weight_kg"
                type="number"
                min={0}
                value={formData.max_weight_kg || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_weight_kg: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="max_volume_m3" className="text-right">
                Vol. Máx (m³)
              </Label>
              <Input
                id="max_volume_m3"
                type="number"
                min={0}
                step={0.01}
                value={formData.max_volume_m3 || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_volume_m3: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="distance_from_shipping" className="text-right">
                Distancia (m)
              </Label>
              <Input
                id="distance_from_shipping"
                type="number"
                min={0}
                step={0.1}
                value={formData.distance_from_shipping || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    distance_from_shipping: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  })
                }
                className="col-span-3"
                placeholder="Distancia al área de despacho"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="ergonomic_score" className="text-right">
                Score Ergonómico
              </Label>
              <Input
                id="ergonomic_score"
                type="number"
                min={0}
                max={100}
                value={formData.ergonomic_score || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    ergonomic_score: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                className="col-span-3"
                placeholder="0-100 (50 = zona dorada)"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="is_active" className="text-right">
                Activa
              </Label>
              <div className="col-span-3">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Guardando..." : position ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
